/**
 * Story 16.4 (FR46) — the low-balance banner, shown on every MainLayout page
 * while the tenant's credit is `low` (at or below CREDIT_LOW_BALANCE_NZD) or
 * `exhausted`. Renders nothing for `ok`, while loading, or on error — including
 * a failed refetch that still holds stale `data` (TanStack keeps it).
 *
 * Owners (`isOwner()` — superuser folded in, matching `RequireOwner` on the
 * link target) get a "View billing" link to Usage & LLM Cost; members and
 * viewers are told to ask their owner and get no link. No balance figure is
 * ever shown — the route does not return one.
 */

import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useCreditStatus } from '@/lib/hooks/useCreditStatus';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { ASK_OWNER_TO_ADD_CREDIT, CREDIT_BILLING_PATH } from '@/lib/billing/creditExhausted';

// Story 16.10 — the path and the ask-owner sentence live in
// `lib/billing/creditExhausted.ts`, shared with every credit-exhausted surface.

export default function CreditBalanceBanner() {
  const { data, isError } = useCreditStatus();
  const { isOwner } = usePermissions();

  if (isError) return null;
  const state = data?.state;
  if (state !== 'low' && state !== 'exhausted') return null;

  const exhausted = state === 'exhausted';
  const owner = isOwner();
  const Icon = exhausted ? XCircleIcon : ExclamationTriangleIcon;
  const sentence = exhausted
    ? 'Your credit has run out — chat and agent work are paused.'
    : 'Your credit is running low.';

  return (
    <div
      role={exhausted ? 'alert' : 'status'}
      data-testid="credit-balance-banner"
      data-state={state}
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-1 text-sm ${
        exhausted
          ? 'border-danger bg-danger-wash text-ink'
          : 'border-warning bg-warning-wash text-warning-ink'
      }`}
    >
      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
      <span className="py-2">
        {sentence}
        {!owner && ` ${ASK_OWNER_TO_ADD_CREDIT}`}
      </span>
      {owner && (
        <Link
          to={CREDIT_BILLING_PATH}
          className={`inline-flex min-h-[44px] items-center font-medium underline underline-offset-2 ${
            exhausted ? 'text-ink hover:text-danger' : 'text-warning-ink hover:text-ink'
          }`}
        >
          View billing
        </Link>
      )}
    </div>
  );
}
